import { supabase } from './supabase';
import type { Country, Profile } from './localData';
import { DocumentHasLinkedResultsError, linkedResultCountFromDeletionError } from './documentDeletion';

const BUCKET = 'medical-documents';
const SIGNED_URL_TTL_SECONDS = 10 * 60;

export type DocumentProcessingStatus = 'uploaded' | 'queued' | 'processing' | 'review' | 'ready' | 'failed';

export const DOCUMENT_PROCESSING_STATUS_LABELS: Record<DocumentProcessingStatus, string> = {
  uploaded: 'Файл загружен',
  queued: 'В очереди',
  processing: 'Обрабатывается',
  review: 'Нужна проверка',
  ready: 'Готов',
  failed: 'Ошибка',
};

export const formatOptionalDocumentDate = (value?: string): string => value
  ? new Date(value).toLocaleDateString('ru-RU')
  : 'не указана';

export const formatOptionalDocumentDateTime = (value?: string): string => value
  ? new Date(value).toLocaleString('ru-RU')
  : 'не указано';

export type DocumentProcessingJobStatus = 'queued' | 'processing' | 'review' | 'completed' | 'failed' | 'cancelled';

export interface DocumentProcessingDiagnostics {
  jobId: string;
  status: DocumentProcessingJobStatus;
  processingVersion?: string;
  totalBatches: number;
  completedBatches: number;
  pendingBatches: number;
  failedBatches: number;
  attempts: number;
  lastProcessedRange?: string;
  lastError?: string;
  startedAt?: string;
  finishedAt?: string;
  updatedAt?: string;
  canRetry: boolean;
  canContinue: boolean;
}

type ProcessingBatchRow = {
  batch_index: number;
  start_page: number;
  end_page: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  attempts: number;
  is_retryable: boolean;
  last_error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
};

const PROCESSING_STALE_AFTER_MS = 10 * 60 * 1000;

// Database/Edge errors can contain stack traces and provider codes. The
// document passport is a patient-facing screen, so keep only a short,
// understandable message there; the full diagnostic remains server-side.
export function publicProcessingError(value?: string | null): string | undefined {
  if (!value) return undefined;
  const message = value
    .replace(/^Error:\s*/i, '')
    .split(/\n\s*(?:at\s|stack\b)/i, 1)[0]
    .trim();
  if (!message || /\b(?:PGRST\d+|SQLSTATE|PostgrestError|SupabaseError)\b/i.test(message)) {
    return 'Не удалось обработать документ. Повторите попытку позже.';
  }
  return message.slice(0, 300);
}

export function makeDocumentProcessingDiagnostics(
  job: {
    id: string;
    status: DocumentProcessingJobStatus;
    processor_version?: string | null;
    total_batches?: number | null;
    retryable?: boolean | null;
    error_message?: string | null;
    started_at?: string | null;
    finished_at?: string | null;
    updated_at?: string | null;
  },
  batches: ProcessingBatchRow[],
  now = Date.now(),
): DocumentProcessingDiagnostics {
  const completed = batches.filter(batch => batch.status === 'completed');
  const pending = batches.filter(batch => batch.status === 'pending');
  const failed = batches.filter(batch => batch.status === 'failed');
  const lastProcessed = [...batches]
    .filter(batch => batch.finished_at || batch.started_at)
    .sort((a, b) => Date.parse(b.finished_at || b.started_at || '') - Date.parse(a.finished_at || a.started_at || ''))[0];
  const updatedAt = job.updated_at || undefined;
  const isStale = job.status === 'processing' && Boolean(updatedAt)
    && now - Date.parse(updatedAt || '') >= PROCESSING_STALE_AFTER_MS;

  return {
    jobId: job.id,
    status: job.status,
    processingVersion: job.processor_version || undefined,
    totalBatches: Number(job.total_batches || batches.length),
    completedBatches: completed.length,
    pendingBatches: pending.length,
    failedBatches: failed.length,
    attempts: batches.reduce((total, batch) => total + Number(batch.attempts || 0), 0),
    lastProcessedRange: lastProcessed ? `${lastProcessed.start_page}–${lastProcessed.end_page} стр.` : undefined,
    lastError: publicProcessingError(job.error_message) || publicProcessingError(failed.at(-1)?.last_error),
    startedAt: job.started_at || undefined,
    finishedAt: job.finished_at || undefined,
    updatedAt,
    canRetry: job.status === 'failed' && job.retryable === true,
    // A stale `processing` job is the durable signal left by an interrupted
    // Edge invocation. The server releases its stale batch atomically.
    canContinue: completed.length > 0 && (job.status === 'queued' || isStale),
  };
}

// Archive documents are a free-standing medical-file store (prescriptions,
// referrals, discharge summaries, certificates, insurance, other) the user
// adds directly, never linked to a lab result -- unlike 'lab_report'
// documents, which are always a byproduct of either the AI extraction
// upload flow or the per-reading "attach file" action.
export type ArchiveCategory = 'prescription' | 'referral' | 'discharge_summary' | 'certificate' | 'insurance' | 'other';

export const ARCHIVE_CATEGORY_LABELS: Record<ArchiveCategory, string> = {
  prescription: 'Рецепт',
  referral: 'Направление',
  discharge_summary: 'Выписка',
  certificate: 'Справка',
  insurance: 'Страховка',
  other: 'Другое',
};

export interface MedicalDocument {
  id: string;
  householdId: string;
  owner: Profile;
  medicalProfileId?: string;
  kind: 'lab_report' | 'archive';
  category?: ArchiveCategory;
  title?: string;
  note?: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  takenOn?: string;
  labName?: string;
  country?: Country;
  createdAt: string;
  processingStatus: DocumentProcessingStatus;
  processingProgress: number;
  pageCount?: number;
  batchCount?: number;
  extractedCount: number;
  confirmedCount: number;
  reviewCount: number;
  failedCount: number;
  processorVersion?: string;
  processingStartedAt?: string;
  processingFinishedAt?: string;
  lastProcessingError?: string;
  laboratoryConclusion?: string;
  laboratoryConclusionPage?: number;
}

export type DocumentDeletionMode = 'document_only' | 'document_and_results';

export interface DocumentDeletionOutcome {
  deletedResultCount: number;
  cancelledJobCount: number;
  /** The document is deleted, but protected Storage cleanup is still pending. */
  storageCleanupPending: boolean;
}

type UploadLabDocumentInput = {
  householdId: string;
  userId: string;
  owner: Profile;
  medicalProfileId?: string;
  country?: Country;
  takenOn?: string;
  labName?: string;
  file: File | Blob;
  fileName?: string;
};

function requireClient() {
  if (!supabase) throw new Error('Supabase не настроен');
  return supabase;
}

function safeFileName(name: string): string {
  const cleaned = name
    .normalize('NFKC')
    .replace(/[\\/]+/g, '-')
    .replace(/[^\p{L}\p{N}._()-]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 120);
  return cleaned || 'medical-document';
}

async function sha256Hex(blob: Blob): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function loadLabDocuments(householdId: string): Promise<MedicalDocument[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('medical_documents')
    .select('id, household_id, owner, medical_profile_id, kind, original_name, mime_type, size_bytes, taken_on, lab_name, country, created_at, processing_status, processing_progress, page_count, extracted_count, confirmed_count, review_count, failed_count, processing_version, processing_started_at, processing_finished_at, last_processing_error, laboratory_conclusion, laboratory_conclusion_page, document_metadata')
    .eq('household_id', householdId)
    .eq('kind', 'lab_report')
    .is('deleted_at', null)
    .order('taken_on', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({
    id: row.id,
    householdId: row.household_id,
    owner: row.owner as Profile,
    medicalProfileId: row.medical_profile_id || undefined,
    kind: 'lab_report',
    originalName: row.original_name,
    mimeType: row.mime_type,
    sizeBytes: row.size_bytes,
    takenOn: row.taken_on || undefined,
    labName: row.lab_name || undefined,
    country: (row.country || 'other') as Country,
    createdAt: row.created_at,
    processingStatus: (row.processing_status || 'uploaded') as DocumentProcessingStatus,
    processingProgress: Number(row.processing_progress || 0),
    pageCount: row.page_count ?? undefined,
    batchCount: typeof row.document_metadata?.totalBatches === 'number' ? row.document_metadata.totalBatches : undefined,
    extractedCount: Number(row.extracted_count || 0),
    confirmedCount: Number(row.confirmed_count || 0),
    reviewCount: Number(row.review_count || 0),
    failedCount: Number(row.failed_count || 0),
    processorVersion: row.processing_version || undefined,
    processingStartedAt: row.processing_started_at || undefined,
    processingFinishedAt: row.processing_finished_at || undefined,
    lastProcessingError: row.last_processing_error || undefined,
    laboratoryConclusion: row.laboratory_conclusion || undefined,
    laboratoryConclusionPage: row.laboratory_conclusion_page ?? undefined,
  }));
}

export async function loadArchiveDocuments(householdId: string): Promise<MedicalDocument[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('medical_documents')
    .select('id, household_id, owner, medical_profile_id, kind, category, title, note, original_name, mime_type, size_bytes, taken_on, created_at, processing_status, processing_progress, extracted_count, confirmed_count, review_count, failed_count')
    .eq('household_id', householdId)
    .eq('kind', 'archive')
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({
    id: row.id,
    householdId: row.household_id,
    owner: row.owner as Profile,
    medicalProfileId: row.medical_profile_id || undefined,
    kind: 'archive',
    category: (row.category || 'other') as ArchiveCategory,
    title: row.title || row.original_name,
    note: row.note || undefined,
    originalName: row.original_name,
    mimeType: row.mime_type,
    sizeBytes: row.size_bytes,
    takenOn: row.taken_on || undefined,
    createdAt: row.created_at,
    processingStatus: (row.processing_status || 'ready') as DocumentProcessingStatus,
    processingProgress: Number(row.processing_progress || 0),
    extractedCount: Number(row.extracted_count || 0),
    confirmedCount: Number(row.confirmed_count || 0),
    reviewCount: Number(row.review_count || 0),
    failedCount: Number(row.failed_count || 0),
  }));
}

export async function loadDocumentProcessingDiagnostics(documentId: string): Promise<DocumentProcessingDiagnostics | null> {
  const client = requireClient();
  const { data: job, error: jobError } = await client
    .from('document_processing_jobs')
    .select('id, status, processor_version, total_batches, retryable, error_message, started_at, finished_at, updated_at')
    .eq('document_id', documentId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (jobError) throw jobError;
  if (!job) return null;

  const { data: batches, error: batchesError } = await client
    .from('document_processing_batches')
    .select('batch_index, start_page, end_page, status, attempts, is_retryable, last_error, started_at, finished_at')
    .eq('job_id', job.id)
    .order('batch_index');
  if (batchesError) throw batchesError;
  return makeDocumentProcessingDiagnostics(job as Parameters<typeof makeDocumentProcessingDiagnostics>[0], (batches || []) as ProcessingBatchRow[]);
}

export async function uploadLabDocument(input: UploadLabDocumentInput): Promise<string> {
  const client = requireClient();
  const blob = input.file;
  const hash = await sha256Hex(blob);

  if (hash) {
    const { data: existing, error: lookupError } = await client
      .from('medical_documents')
      .select('id')
      .eq('household_id', input.householdId)
      .eq('sha256', hash)
      .is('deleted_at', null)
      .maybeSingle();
    if (lookupError) throw lookupError;
    if (existing?.id) return existing.id;
  }

  const documentId = crypto.randomUUID();
  const fileName = safeFileName(input.fileName || (input.file instanceof File ? input.file.name : 'medical-document'));
  const storagePath = `${input.householdId}/labs/${documentId}/${fileName}`;

  const { error: uploadError } = await client.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: blob.type,
    cacheControl: '3600',
    upsert: false,
  });
  if (uploadError) throw uploadError;

  const { error: metadataError } = await client.from('medical_documents').insert({
    id: documentId,
    household_id: input.householdId,
    owner: input.owner,
    medical_profile_id: input.medicalProfileId || null,
    kind: 'lab_report',
    storage_path: storagePath,
    original_name: input.fileName || fileName,
    mime_type: blob.type || 'application/octet-stream',
    size_bytes: blob.size,
    sha256: hash,
    taken_on: input.takenOn || null,
    lab_name: input.labName || null,
    country: input.country || null,
    created_by: input.userId,
    processing_status: 'uploaded',
    processing_progress: 0,
  });

  if (metadataError) {
    await client.storage.from(BUCKET).remove([storagePath]);
    throw metadataError;
  }

  return documentId;
}

type UploadArchiveDocumentInput = {
  householdId: string;
  userId: string;
  owner: Profile;
  medicalProfileId?: string;
  category: ArchiveCategory;
  title: string;
  note?: string;
  takenOn?: string;
  file: File | Blob;
  fileName?: string;
};

export async function uploadArchiveDocument(input: UploadArchiveDocumentInput): Promise<string> {
  const client = requireClient();
  const blob = input.file;
  const hash = await sha256Hex(blob);

  if (hash) {
    const { data: existing, error: lookupError } = await client
      .from('medical_documents')
      .select('id')
      .eq('household_id', input.householdId)
      .eq('sha256', hash)
      .is('deleted_at', null)
      .maybeSingle();
    if (lookupError) throw lookupError;
    if (existing?.id) return existing.id;
  }

  const documentId = crypto.randomUUID();
  const fileName = safeFileName(input.fileName || (input.file instanceof File ? input.file.name : 'medical-document'));
  const storagePath = `${input.householdId}/archive/${documentId}/${fileName}`;

  const { error: uploadError } = await client.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: blob.type,
    cacheControl: '3600',
    upsert: false,
  });
  if (uploadError) throw uploadError;

  const { error: metadataError } = await client.from('medical_documents').insert({
    id: documentId,
    household_id: input.householdId,
    owner: input.owner,
    medical_profile_id: input.medicalProfileId || null,
    kind: 'archive',
    category: input.category,
    title: input.title.trim() || null,
    note: input.note?.trim() || null,
    storage_path: storagePath,
    original_name: input.fileName || fileName,
    mime_type: blob.type || 'application/octet-stream',
    size_bytes: blob.size,
    sha256: hash,
    taken_on: input.takenOn || null,
    created_by: input.userId,
    processing_status: 'ready',
    processing_progress: 100,
  });

  if (metadataError) {
    await client.storage.from(BUCKET).remove([storagePath]);
    throw metadataError;
  }

  return documentId;
}

export async function finalizeLabDocument(documentId: string, metadata: {
  takenOn: string;
  labName?: string;
  country: Country;
  confirmedCount: number;
  reviewCount: number;
}): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('medical_documents').update({
    taken_on: metadata.takenOn,
    lab_name: metadata.labName || null,
    country: metadata.country,
    confirmed_count: metadata.confirmedCount,
    review_count: metadata.reviewCount,
    processing_status: 'ready',
    processing_progress: 100,
    processing_finished_at: new Date().toISOString(),
    last_processing_error: null,
  }).eq('id', documentId).is('deleted_at', null);
  if (error) throw error;
}

// Keeps the document passport's own taken_on/lab_name in sync after a bulk
// edit of its readings (see bulkUpdateLabResultMetadataForDocument) — the
// two are separate copies (one row here vs. one per reading), and the
// passport is what "Документы" displays and searches/filters by.
export async function updateLabDocumentMetadata(documentId: string, patch: { takenOn?: string; labName?: string }): Promise<void> {
  const client = requireClient();
  const update: Record<string, unknown> = {};
  if (patch.takenOn !== undefined) update.taken_on = patch.takenOn;
  if (patch.labName !== undefined) update.lab_name = patch.labName || null;
  if (!Object.keys(update).length) return;
  const { error } = await client.from('medical_documents').update(update).eq('id', documentId).is('deleted_at', null);
  if (error) throw error;
}

export async function getLabDocumentSignedUrl(documentId: string): Promise<string> {
  const client = requireClient();
  const { data: document, error: documentError } = await client
    .from('medical_documents')
    .select('storage_path')
    .eq('id', documentId)
    .is('deleted_at', null)
    .single();
  if (documentError) throw documentError;

  const { data, error } = await client.storage
    .from(BUCKET)
    .createSignedUrl(document.storage_path, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
}

export async function openLabSourceDocument(documentId?: string, legacySource?: string): Promise<void> {
  if (documentId) {
    const url = await getLabDocumentSignedUrl(documentId);
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (legacySource) {
    window.open(legacySource, '_blank', 'noopener,noreferrer');
    return;
  }
  throw new Error('Исходный файл для этого анализа не найден');
}

// Shares the original file itself, not a signed URL. The document remains in
// the private bucket: the current family member must pass the same RLS checks
// used by the normal "open original" flow before a File reaches Web Share.
// Desktop browsers without Web Share receive a normal local download instead.
export async function shareLabSourceDocument(documentId: string, originalName: string): Promise<'shared' | 'downloaded'> {
  const client = requireClient();
  const { data: document, error: documentError } = await client
    .from('medical_documents')
    .select('storage_path, mime_type')
    .eq('id', documentId)
    .is('deleted_at', null)
    .single();
  if (documentError) throw documentError;

  const { data: blob, error: downloadError } = await client.storage.from(BUCKET).download(document.storage_path);
  if (downloadError) throw downloadError;

  const file = new File([blob], originalName || 'lab-report', {
    type: blob.type || document.mime_type || 'application/octet-stream',
  });
  const payload = { files: [file], title: file.name };
  if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(payload))) {
    await navigator.share(payload);
    return 'shared';
  }

  const url = URL.createObjectURL(file);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.hidden = true;
  window.document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return 'downloaded';
}

export async function linkLabResultsToDocument(resultIds: string[], documentId: string): Promise<void> {
  if (!resultIds.length) return;
  const client = requireClient();
  const { error } = await client
    .from('lab_results')
    .update({ document_id: documentId, source_document: null })
    .in('id', resultIds)
    .is('deleted_at', null);
  if (error) throw error;
}

export async function deleteLabDocument(documentId: string, mode: DocumentDeletionMode): Promise<DocumentDeletionOutcome> {
  const client = requireClient();
  const { data, error } = await client.functions.invoke('delete-lab-document', {
    body: { documentId, mode },
  });
  if (error) {
    let message = error.message;
    let payload: unknown;
    const context = 'context' in error ? error.context : undefined;
    if (context && typeof context === 'object' && 'clone' in context && typeof context.clone === 'function') {
      try {
        payload = await context.clone().json();
        if (payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string') message = payload.error;
      } catch {
        // The SDK's generic error remains a safe fallback.
      }
    }
    const linkedResultCount = linkedResultCountFromDeletionError(payload) ?? linkedResultCountFromDeletionError({ error: message });
    if (linkedResultCount !== undefined) throw new DocumentHasLinkedResultsError(linkedResultCount);
    throw new Error(message);
  }
  return {
    deletedResultCount: Number(data?.deletedResultCount || 0),
    cancelledJobCount: Number(data?.cancelledJobCount || 0),
    storageCleanupPending: Boolean(data?.storageCleanupPending),
  };
}


export async function getLabDocumentDataUrl(documentId: string): Promise<string> {
  const client = requireClient();
  const { data: document, error: documentError } = await client
    .from('medical_documents')
    .select('storage_path, mime_type')
    .eq('id', documentId)
    .is('deleted_at', null)
    .single();
  if (documentError) throw documentError;

  const { data: blob, error: downloadError } = await client.storage.from(BUCKET).download(document.storage_path);
  if (downloadError) throw downloadError;

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Не удалось прочитать сохранённый документ'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob.type ? blob : new Blob([blob], { type: document.mime_type || 'application/octet-stream' }));
  });
}
